import classNames from 'classnames';
import map from 'lodash/map';
import { Col, Row } from 'reactstrap';
import InlineSVG from 'svg-inline-react';

import pillHeart from 'public/pill-heart-animated.svg';
import pillHeart2 from 'public/pill-heart2.svg';
import stethoscope from 'public/stethoscope.svg';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { Typography } from 'src/app/components/Elements/Typography';
import HowItWorksSection from 'src/app/components/PageSections/HowItWorksSection';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';

import LeadForm from './LeadForm/container';
import { ColorSpan, FieldSet, LegendTitle, MainTitle } from './styles';
import styles from './styles.module.scss';

const mediaLogos = [
  {
    src: '/public/medium.png',
    href: 'https://medium.com/capsule-cares/everybody-needs-some-looking-after-sometimes-1cf62ba465ba?utm_source=capsule.com&utm_content=fordoctors',
    alt: 'Medium logo icon',
  },
  {
    src: '/public/fortune.png',
    href: 'http://fortune.com/2016/05/17/capsule-digital-pharmacy/',
    alt: 'Fortune logo icon',
  },
  {
    src: '/public/business-insider.png',
    href: 'http://www.businessinsider.com/what-is-capsule-pharmacy-and-its-medicine-delivery-app-2016-5',
    alt: 'Business Insider logo icon',
  },
  {
    src: '/public/tc.png',
    href: 'https://techcrunch.com/2016/05/17/capsule-launches-to-reinvent-the-pharmacy-complete-with-med-delivery/',
    alt: 'TechCrunch logo icon',
  },
  {
    src: '/public/nyp.png',
    href: 'http://nypost.com/2016/05/17/heres-a-drug-delivery-service-with-a-twist/',
    alt: 'New York Post logo icon',
  },
  {
    src: '/public/cnbc.png',
    href: 'http://www.cnbc.com/2016/05/16/digital-focused-pharmacy-capsule-opens-in-new-york.html',
    alt: 'CNBC logo icon',
  },
  {
    src: '/public/abc.png',
    href: 'http://abc7ny.com/technology/capsule-app-looks-to-take-lines-hassle-out-of-prescriptions/1462981/',
    alt: 'ABC logo icon',
  },
];

const Doctors = () => {
  useSegmentTrack(UNIFIED.viewForDoctors);
  return (
    <div id="about-content">
      <MarketingPageSection>
        <Row>
          <InlineSVG src={stethoscope} className={classNames(styles.stethoscope, 'lg-show')} />
          <Col xs="12" md="9" lg="7">
            <FieldSet>
              <MainTitle className="font-h-3">Join the thousands of doctors using Capsule</MainTitle>
              <p className="mt-2 mb-4">
                We&#8217;ll contact you to tell you more about how you can partner with Capsule.
              </p>
              <LeadForm
                className={styles.formOne}
                inputStyle={styles.inputFieldOne}
                buttonContainerStyle={styles.buttonOne}
                source="static_doctors_lead_form_one"
                formName="first-lead-form"
              />
            </FieldSet>
          </Col>
        </Row>
      </MarketingPageSection>
      <MarketingPageSection className="section--dark">
        <Row>
          <InlineSVG className={classNames(styles.pillHeart, styles.red, 'md-show')} src={pillHeart} />
          <InlineSVG className={classNames(styles.pillHeart, styles.yellow, 'md-show')} src={pillHeart2} />
          <InlineSVG className={classNames(styles.pillHeart, styles.teal, 'md-show')} src={pillHeart} />
          <Col xs="12" md="11" lg="10">
            <h2 className="font-h-4 white">
              Doctors <ColorSpan color={'capsuleRed10'}>love</ColorSpan> Capsule
            </h2>
            <p className={classNames(styles.borderBottom, 'teal', 'mb-3', 'pb-5')}>
              The new standard in care. Seamless patient experience. <br className="md-show" />
              Dynamic inventory management. Adherence reporting.
            </p>
            <h2 className="font-h-4 white">
              Staffs <ColorSpan color={'capsuleYellow'}>love</ColorSpan> Capsule
            </h2>
            <p className={classNames(styles.borderBottom, 'teal', 'mb-3', 'pb-5')}>
              <>
                Already in your EMR. Dedicated phone lines.
                <br className="md-show" />
                <span className="md-hide"> </span>
                Prior authorization technology.
              </>
            </p>
            <h2 className="font-h-4 white">
              Patients <ColorSpan color={'capsuleGreen50'}>love</ColorSpan> Capsule
            </h2>
            <p className="teal">
              <>
                The best pharmacy. Free delivery within 2 hours.
                <br className="md-show" />
                No extra costs. Copay stays the same.
              </>
            </p>
          </Col>
        </Row>
      </MarketingPageSection>

      <HowItWorksSection />

      <MarketingPageSection className="section--dark">
        <Row>
          <Col xs="12" md="9" lg="7">
            <FieldSet>
              <LegendTitle className="white font-h-3 mb-0">Join the pharmacy of the future</LegendTitle>
              <p className="teal mt-2 mb-5">
                We&#8217;ll contact you within one business day to tell you
                <br className="md-show" /> more about how you can partner with Capsule.
              </p>
              <LeadForm
                className={styles.formTwo}
                inputStyle={styles.inputFieldTwo}
                buttonContainerStyle={styles.buttonTwo}
                source="static_doctors_lead_form_two"
                formName="second-lead-form"
                isWhite
                hasLightRedError
              />
            </FieldSet>
          </Col>
        </Row>
      </MarketingPageSection>
      <MarketingPageSection>
        <Row>
          <Col xs="12" md="9">
            <p className={classNames(styles.marketingText, 'font-label-callout uppercase font-down-2')}>
              Word is spreading
            </p>
            <Typography variant="displayXL" color="capsuleRed60" marginBottom="s5">
              Capsule is setting the new standard in care
            </Typography>
            <p className="green font-b-1">
              &lsquo;This is a unique moment and opportunity to build a pharmacy that works for everyone - one that is
              fully integrated for consumers, physicians, hospitals, payors, and manufacturers.&rsquo;
            </p>
          </Col>
          <Col xs="12">
            <div className={styles.mediaLogoContainer}>
              {map(mediaLogos, logo => {
                return (
                  <a key={logo.href} className={styles.logo} href={logo.href} target="_blank">
                    <img className={logo.css} src={logo.src} alt={logo.alt} />
                  </a>
                );
              })}
            </div>
          </Col>
        </Row>
      </MarketingPageSection>
    </div>
  );
};

export default Doctors;
